import { useEffect, useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Icon } from "@/components/Icons";
import {
  RelationshipResults,
  type SignalRelationshipResult,
} from "@/components/RelationshipResults";
import { longDate } from "@/features/signals/format";
import type { EvidenceInfo, RecipientBrief } from "../types";
import type { RecipientActionMutations } from "../hooks";
import { WhyForYou } from "./WhyForYou";
import { AccountPatternBanner } from "./AccountPatternBanner";

/**
 * Recipient intelligence column (Reskin Session C), sealed anatomy: why-for-you
 * and account pattern above, then the zone 2 intelligence block (summary, why it
 * matters, recommended action, talk track, on-demand runs), the zone 3 evidence
 * block (recommended asset + evidence table), the matched-contacts zone, and the
 * The authenticated Research Assistant is intentionally not exposed through a
 * recipient token.
 *
 * On-demand runs record a ledger event; the relationship check also returns its
 * grouped-by-owner result, rendered below the run buttons identical to the
 * internal modal. The tariff scan still records only the event (no verdict).
 */
export function RecipientMain({
  brief,
  actions,
}: {
  brief: RecipientBrief;
  actions: RecipientActionMutations;
}): JSX.Element {
  const { signal, evidence } = brief;
  const asset = brief.recommendedAsset;

  // Capture the relationship-check result into local state on the success edge
  // (the ToolButton resets its mutation ~2.6s after success). Rendered identical
  // to the internal modal; "Pass signal to X" is disabled-honest here because a
  // token recipient cannot assign (no pass prop supplied).
  const [relationships, setRelationships] = useState<SignalRelationshipResult | null>(null);
  const relData = actions.relationshipCheck.data;
  useEffect(() => {
    if (relData) setRelationships(relData);
  }, [relData]);

  return (
    <>
      <WhyForYou why={brief.whyForYou} />
      <AccountPatternBanner pattern={brief.accountPattern} />

      {/* ZONE 2 · Intelligence */}
      <div className="sx-z-intel">
        <p className="sx-eyebrow">Summary</p>
        <p className="sx-body">{signal.summary}</p>

        <p className="sx-eyebrow">Why it matters</p>
        <p className="sx-body">{signal.whyItMatters}</p>

        <p className="sx-eyebrow sx-eyebrow-action">Recommended action</p>
        <p className="sx-body">{signal.recommendedAction}</p>

        <div className="sx-talk">
          <p className="sx-eyebrow">Talk track</p>
          <p className="sx-body">{signal.talkTrack}</p>
        </div>

        <div className="sx-tools" style={{ gridTemplateColumns: "1fr" }}>
          <ToolButton
            label="Run Relationship Check in the firm"
            help="Find colleague relationships"
            recordedNote="Relationship check recorded"
            mutation={actions.relationshipCheck}
          />
        </div>
        {relationships ? (
          <RelationshipResults result={relationships} fallbackCompany={signal.company} />
        ) : null}
      </div>

      {/* ZONE 3 · Evidence and assets */}
      <div className="sx-z-evidence">
        {/* Recommended asset (parity amendment Item 1) — honest empty state when
            no real asset data exists (currently always, on live and mock). The
            populated card stays dormant, ready for the Attention Object Library
            slice; this matches the internal modal exactly. */}
        <p className="sx-zone-title">Lead magnet</p>
        {asset ? (
          <div className="sx-asset">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#5E7472"
              strokeWidth="1.6"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14 3v5h5" />
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            </svg>
            <div className="sx-a-body">
              <p className="sx-a-name">{asset.title}</p>
              <p className="sx-a-fit">{`${asset.kind} · ${asset.reason}`.toUpperCase()}</p>
            </div>
            <button
              type="button"
              className="sx-btn sx-btn-p1"
              style={{ padding: "6px 14px" }}
              disabled={actions.sendAsset.isPending}
              onClick={() => actions.sendAsset.mutate(asset.title)}
            >
              {actions.sendAsset.isSuccess
                ? "Sent"
                : actions.sendAsset.isPending
                  ? "Sending…"
                  : "Send"}
            </button>
          </div>
        ) : (
          <div className="sx-lead-magnet" aria-label="Recommended lead signal coming soon">
            <div>
              <p className="sx-lead-magnet-title">Recommended lead signal</p>
              <p className="sx-lead-magnet-copy">
                A relevant conversation starter will be matched to this signal.
              </p>
            </div>
            <span className="sx-lead-magnet-chip">Coming soon</span>
          </div>
        )}

        <p className="sx-zone-title sx-sm">Evidence and verification</p>
        <EvidenceTable evidence={evidence} />
        {evidence.sourceUrl ? (
          <div className="sxr-evidence-actions">
            <a
              className="sx-btn sx-btn-p2"
              href={evidence.sourceUrl}
              target="_blank"
              rel="noreferrer"
            >
              View source
              <Icon name="external-link" />
            </a>
          </div>
        ) : null}
      </div>
    </>
  );
}

const VERIF_LABEL: Record<EvidenceInfo["status"], string> = {
  verified: "Verified",
  unverified: "Unverified",
  flagged: "Flagged",
};

function EvidenceTable({ evidence }: { evidence: EvidenceInfo }): JSX.Element {
  return (
    <table className="sx-ev-table">
      <caption className="sx-sr-only">Evidence and verification for this signal</caption>
      <tbody>
        <tr>
          <td>Source</td>
          <td>
            {evidence.sourceUrl ? (
              <a href={evidence.sourceUrl} target="_blank" rel="noreferrer">
                {evidence.sourceLabel}
                <Icon name="external-link" />
              </a>
            ) : (
              evidence.sourceLabel
            )}
          </td>
        </tr>
        <tr>
          <td>Retrieved</td>
          <td>{longDate(evidence.retrievedAt) ?? "Unknown"}</td>
        </tr>
        <tr>
          <td>Status</td>
          <td>{VERIF_LABEL[evidence.status]}</td>
        </tr>
        <tr>
          <td>Deadline</td>
          <td>{evidence.deadlineAt ? longDate(evidence.deadlineAt) : "None noted"}</td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * On-demand tool button (sealed .tool). Wired to the recipient ledger action
 * (record_magic_link_recipient_action). The button itself only confirms the run
 * was recorded; for the relationship check the grouped-by-owner result is
 * rendered by RecipientMain in a separate panel below this row. The tariff scan
 * records only the event (no verdict payload exists for it).
 */
function ToolButton<TData>({
  label,
  help,
  recordedNote,
  mutation,
}: {
  label: string;
  help: string;
  recordedNote: string;
  mutation: UseMutationResult<TData, Error, void, unknown>;
}): JSX.Element {
  const { isPending, isSuccess, isError, reset, mutate } = mutation;
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    if (!isSuccess) return;
    setRecorded(true);
    const timer = window.setTimeout(() => {
      setRecorded(false);
      reset();
    }, 2600);
    return () => window.clearTimeout(timer);
  }, [isSuccess, reset]);

  return (
    <button
      type="button"
      className="sx-tool"
      disabled={isPending}
      onClick={() => {
        reset();
        setRecorded(false);
        mutate();
      }}
    >
      {isPending ? "Running…" : recorded ? "Recorded ✓" : label}
      <span className={`sx-sub${recorded ? " sx-sub--ok" : ""}${isError ? " sx-sub--err" : ""}`}>
        {isError ? "Couldn't record — try again" : recorded ? recordedNote : help}
      </span>
    </button>
  );
}
